import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getConsent,
  setConsent,
  initAnalytics,
  updateConsent,
  onOpenConsentSettings,
} from '../lib/analytics'

/**
 * EduMisia cookie consent banner.
 *
 * - Renders on first visit (no stored choice) with two actions: Súhlasím
 *   (accept analytics) and Odmietnuť (deny).
 * - Never touches ads/marketing consent — they remain denied always.
 * - Can be reopened from anywhere via `openConsentSettings()` (used by the
 *   footer link "Nastavenia cookies").
 */
const CookieBanner = () => {
  const [visible, setVisible] = useState(false)
  const [current, setCurrent] = useState(null)

  useEffect(() => {
    const stored = getConsent()
    setCurrent(stored)
    if (!stored) setVisible(true)
    // If user has already consented to analytics, bootstrap GA4 on load.
    if (stored?.analytics === true) initAnalytics(stored)
  }, [])

  useEffect(() => onOpenConsentSettings(() => {
    setCurrent(getConsent())
    setVisible(true)
  }), [])

  const handle = (analytics) => {
    const rec = setConsent({ analytics })
    setCurrent(rec)
    if (analytics) initAnalytics(rec)
    else updateConsent(rec)
    setVisible(false)
  }

  if (!visible) return null

  const isReopen = !!current // has an existing choice → we're in "settings" mode
  const bannerLabel = isReopen ? 'Nastavenia cookies' : 'Cookies na EduMisii'

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-3 bottom-3 sm:inset-x-4 sm:bottom-4 z-50 max-w-3xl sm:mx-auto rounded-2xl bg-white/95 backdrop-blur ring-1 ring-slate-200 shadow-xl p-4 sm:p-5"
      data-testid="cookie-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <p id="cookie-banner-title" className="text-sm font-bold text-slate-900 mb-1">
            {bannerLabel}
          </p>
          <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
            Používame analytické cookies na základné meranie návštevnosti a zlepšovanie EduMisie. Reklamné cookies nepoužívame.{' '}
            <Link
              to="/privacy-policy#cookies"
              className="text-blue-700 font-semibold hover:text-blue-800 underline underline-offset-2"
              data-testid="cookie-banner-more-info"
            >
              Viac info
            </Link>
          </p>
          {isReopen && (
            <p className="mt-1 text-[11px] text-slate-500" data-testid="cookie-banner-current">
              Aktuálne nastavenie:{' '}
              <span className="font-semibold text-slate-700">
                {current.analytics ? 'Analytika povolená' : 'Analytika zamietnutá'}
              </span>
            </p>
          )}
        </div>
        <div className="flex gap-2 sm:flex-col sm:items-stretch shrink-0">
          <button
            type="button"
            onClick={() => handle(false)}
            className="flex-1 sm:flex-none px-4 py-2 rounded-xl text-sm font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50"
            data-testid="cookie-banner-reject"
          >
            Odmietnuť
          </button>
          <button
            type="button"
            onClick={() => handle(true)}
            className="flex-1 sm:flex-none px-4 py-2 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm"
            data-testid="cookie-banner-accept"
          >
            Súhlasím
          </button>
        </div>
      </div>
    </div>
  )
}

export default CookieBanner
